import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, Link as RouterLink } from "react-router-dom";
import {
  Box,
  Grid,
  Card,
  CardActionArea,
  CardContent,
  Typography,
  IconButton,
  Tooltip,
  Modal,
  Paper,
  TextField,
  Button,
  Stack,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  InputAdornment,
} from "@mui/material";
import FolderIcon from "@mui/icons-material/Folder";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import CloseIcon from "@mui/icons-material/Close";
import moment from "moment";
import DashboardLayout from "../../../layouts/DashboardLayout";
import PageHeader from "../../../components/layout/PageHeader";
import axios from "../../../config/axiosConfig";

const FORM_VACIO = { id: null, nombre: "", descripcion: "" };
const normalize = (v) => String(v || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").trim().toLowerCase();

const DocumentosPage = () => {
  const navigate = useNavigate();
  const [carpetas, setCarpetas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [openModal, setOpenModal] = useState(false);
  const [form, setForm] = useState(FORM_VACIO);
  const [saving, setSaving] = useState(false);

  const [busqueda, setBusqueda] = useState("");
  const [archivosEncontrados, setArchivosEncontrados] = useState([]);
  const [buscandoArchivos, setBuscandoArchivos] = useState(false);

  const isEdit = Boolean(form.id);

  const fetchCarpetas = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get("/api/documentos/carpetas");
      setCarpetas(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Error al obtener carpetas:", err);
      setError("No se pudieron cargar las carpetas.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCarpetas();
  }, []);

  const porId = useMemo(() => new Map(carpetas.map((c) => [c.id, c])), [carpetas]);

  // "Padre / Hijo" de las carpetas que contienen a `carpeta` (vacío si es raíz).
  const rutaPadres = (carpeta) => {
    const nombres = [];
    let padre = porId.get(carpeta.padre_id);
    while (padre && nombres.length < 20) {
      nombres.unshift(padre.nombre);
      padre = porId.get(padre.padre_id);
    }
    return nombres.join(" / ");
  };

  // Sin búsqueda se ven solo las carpetas raíz; al buscar también aparecen las subcarpetas que coinciden.
  const carpetasFiltradas = useMemo(() => {
    const q = normalize(busqueda);
    if (!q) return carpetas.filter((c) => !c.padre_id);
    return carpetas.filter(
      (c) => normalize(c.nombre).includes(q) || normalize(c.descripcion).includes(q)
    );
  }, [carpetas, busqueda]);

  useEffect(() => {
    const q = busqueda.trim();
    if (!q) {
      setArchivosEncontrados([]);
      return;
    }
    setBuscandoArchivos(true);
    const timer = setTimeout(() => {
      axios
        .get("/api/documentos/archivos/buscar", { params: { q } })
        .then(({ data }) => setArchivosEncontrados(Array.isArray(data) ? data : []))
        .catch((err) => console.error("Error al buscar archivos:", err))
        .finally(() => setBuscandoArchivos(false));
    }, 350);
    return () => clearTimeout(timer);
  }, [busqueda]);

  const handleOpenCrear = () => {
    setForm(FORM_VACIO);
    setOpenModal(true);
  };

  const handleOpenEditar = (e, carpeta) => {
    e.stopPropagation();
    setForm({ id: carpeta.id, nombre: carpeta.nombre, descripcion: carpeta.descripcion || "" });
    setOpenModal(true);
  };

  const handleGuardar = async (e) => {
    e.preventDefault();
    if (!form.nombre.trim()) return;
    setSaving(true);
    try {
      if (isEdit) {
        await axios.put(`/api/documentos/carpetas/${form.id}`, {
          nombre: form.nombre.trim(),
          descripcion: form.descripcion.trim(),
        });
      } else {
        await axios.post("/api/documentos/carpetas", {
          nombre: form.nombre.trim(),
          descripcion: form.descripcion.trim(),
        });
      }
      setForm(FORM_VACIO);
      setOpenModal(false);
      fetchCarpetas();
    } catch (err) {
      console.error("Error al guardar carpeta:", err);
      setError("No se pudo guardar la carpeta.");
    } finally {
      setSaving(false);
    }
  };

  const handleEliminarCarpeta = async (e, carpeta) => {
    e.stopPropagation();
    if (carpeta.total_archivos > 0 || carpeta.total_subcarpetas > 0) return;
    if (!window.confirm(`¿Eliminar la carpeta "${carpeta.nombre}"? Esta acción no se puede deshacer.`)) {
      return;
    }
    try {
      await axios.delete(`/api/documentos/carpetas/${carpeta.id}`);
      fetchCarpetas();
    } catch (err) {
      console.error("Error al eliminar carpeta:", err);
      setError(err?.response?.data?.error || "No se pudo eliminar la carpeta.");
    }
  };

  return (
    <DashboardLayout>
      <PageHeader
        title="Documentos"
        subtitle="Manuales, políticas y archivos compartidos por tema"
        actions={
          <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={handleOpenCrear}>
            Nueva carpeta
          </Button>
        }
      />

      <Box sx={{ p: { xs: 1.5, md: 2 } }}>
        {error && <Typography color="error" sx={{ mb: 2 }}>{error}</Typography>}

        <TextField
          placeholder="Buscar carpetas o archivos..."
          value={busqueda}
          onChange={(e) => setBusqueda(e.target.value)}
          fullWidth
          size="small"
          sx={{ mb: 3, maxWidth: 480 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: busqueda && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setBusqueda("")}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        {busqueda.trim() && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Archivos encontrados {buscandoArchivos ? "..." : `(${archivosEncontrados.length})`}
            </Typography>
            {archivosEncontrados.length > 0 ? (
              <Paper variant="outlined">
                <List disablePadding>
                  {archivosEncontrados.map((archivo) => (
                    <ListItemButton
                      key={archivo.id}
                      component={RouterLink}
                      to={`/documentos/${archivo.carpeta_id}`}
                    >
                      <ListItemIcon>
                        <InsertDriveFileIcon />
                      </ListItemIcon>
                      <ListItemText
                        primary={archivo.nombre}
                        secondary={`En "${archivo.carpeta_nombre}"${archivo.descripcion ? ` • ${archivo.descripcion}` : ""}`}
                      />
                    </ListItemButton>
                  ))}
                </List>
              </Paper>
            ) : (
              !buscandoArchivos && (
                <Typography variant="body2" color="text.secondary">
                  No se encontraron archivos que coincidan.
                </Typography>
              )
            )}
          </Box>
        )}

        {!loading && carpetasFiltradas.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            {busqueda.trim() ? "Ninguna carpeta coincide con la búsqueda." : "No hay carpetas todavía. Crea la primera con \"Nueva carpeta\"."}
          </Typography>
        ) : (
          <Grid container spacing={2}>
            {carpetasFiltradas.map((carpeta) => {
              const ruta = rutaPadres(carpeta);
              const ocupada = carpeta.total_archivos > 0 || carpeta.total_subcarpetas > 0;
              return (
              <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={carpeta.id}>
                <Card variant="outlined" sx={{ height: "100%" }}>
                  <CardActionArea onClick={() => navigate(`/documentos/${carpeta.id}`)} sx={{ height: "100%", alignItems: "flex-start" }}>
                    <CardContent sx={{ display: "flex", gap: 1.5 }}>
                      <FolderIcon sx={{ color: "success.main", fontSize: 36, flexShrink: 0 }} />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body1" sx={{ fontWeight: 600 }} noWrap>
                          {carpeta.nombre}
                        </Typography>
                        {ruta && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: "block" }} noWrap>
                            En {ruta}
                          </Typography>
                        )}
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                          {carpeta.total_archivos} archivo{carpeta.total_archivos === 1 ? "" : "s"}
                          {carpeta.total_subcarpetas > 0 &&
                            ` • ${carpeta.total_subcarpetas} subcarpeta${carpeta.total_subcarpetas === 1 ? "" : "s"}`}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                          Creado {moment(carpeta.fecha).format("DD/MM/YYYY")}
                        </Typography>
                        {carpeta.descripcion && (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              display: "-webkit-box",
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: "vertical",
                              overflow: "hidden",
                            }}
                          >
                            {carpeta.descripcion}
                          </Typography>
                        )}
                      </Box>
                      <Stack sx={{ flexShrink: 0 }}>
                        <Tooltip title="Editar">
                          <IconButton size="small" onClick={(e) => handleOpenEditar(e, carpeta)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={ocupada ? "No se puede eliminar: tiene archivos o subcarpetas dentro" : "Eliminar carpeta"}>
                          <span>
                            <IconButton
                              size="small"
                              disabled={ocupada}
                              onClick={(e) => handleEliminarCarpeta(e, carpeta)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Stack>
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Grid>
              );
            })}
          </Grid>
        )}
      </Box>

      <Modal open={openModal} onClose={() => setOpenModal(false)}>
        <Paper
          component="form"
          onSubmit={handleGuardar}
          sx={{
            position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)",
            width: 460, maxWidth: "90vw", p: 3, borderRadius: 2,
          }}
        >
          <Typography variant="h6" sx={{ mb: 2 }}>{isEdit ? "Editar carpeta" : "Nueva carpeta"}</Typography>
          <Stack spacing={2}>
            <TextField
              label="Nombre"
              value={form.nombre}
              onChange={(e) => setForm({ ...form, nombre: e.target.value })}
              fullWidth
              autoFocus
              required
            />
            <TextField
              label="Descripción"
              value={form.descripcion}
              onChange={(e) => setForm({ ...form, descripcion: e.target.value })}
              fullWidth
              multiline
              minRows={3}
              placeholder="De qué trata este tema, para orientar a quien lo abra..."
            />
          </Stack>
          <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 2 }}>
            <Button onClick={() => setOpenModal(false)}>Cancelar</Button>
            <Button type="submit" variant="contained" disabled={saving}>
              {saving ? "Guardando..." : isEdit ? "Guardar cambios" : "Crear"}
            </Button>
          </Stack>
        </Paper>
      </Modal>
    </DashboardLayout>
  );
};

export default DocumentosPage;
